import { z } from 'zod';

import type { EnhancedToolResult, ToolExecutionContext } from '../types';

import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import { TextPositionRecommendationOptions } from 'js/src/client/types.gen';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';
import { setSlideTextPositionOperation } from 'js/src/luminary/text-editing-operations';

type TextPositionPreset = Exclude<`${TextPositionRecommendationOptions}`, 'auto'>;

// Every preset except 'auto' — the model picks a concrete position, not "auto".
// Kept in enum-declaration order so the emitted enum matches the previous schema.
const TEXT_POSITION_PRESET_VALUES = Object.values(TextPositionRecommendationOptions).filter(
    (value) => value !== TextPositionRecommendationOptions.AUTO,
) as [TextPositionPreset, ...TextPositionPreset[]];

export const setTextPositionPresetSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    text_position_preset: z
        .enum(TEXT_POSITION_PRESET_VALUES)
        .describe('The text position preset. Not all scenes support all presets.'),
});
export type SetTextPositionPresetArgs = z.infer<typeof setTextPositionPresetSchema>;

export class SetTextPositionPresetTool extends VideoStateTool<typeof setTextPositionPresetSchema> {
    public static readonly toolName = 'set_text_position_preset';

    protected override readonly schema = setTextPositionPresetSchema;
    protected override readonly toolName = SetTextPositionPresetTool.toolName;
    protected override readonly toolDescription =
        'Set text position preset for a scene. Coarser adjustments than manually overriding asset positions. Before calling this tool, read the scene\'s "textPositionPresets" field in the current video state and pass one of those values. Remember! This is a high level tool, so it should always be your first choice when adjusting text positions!';

    public override getToolDisplayName(): string {
        return 'Setting text position preset';
    }

    public get statusIcon(): string {
        return 'format_align_center';
    }

    public async execute(args: SetTextPositionPresetArgs, context: ToolExecutionContext) {
        const { text_position_preset } = args;
        const { parentActionGroup } = context;

        const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
            this.luminary,
            this.getState(),
            args,
        );
        const scene = this.luminary.scenes[scene_index];

        // Set up operations
        const { setSlideTextPosition } = makeOperationsCallable(
            {
                setSlideTextPositionOperation,
            },
            this.luminary,
            this.dispatch,
            this.getState as GetRootState,
        );

        try {
            setSlideTextPosition(scene.id, text_position_preset, false, parentActionGroup);
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : 'Unknown error occurred';
            const errorPayload = {
                error: errorMessage,
                scene_index,
                requested_text_position_preset: text_position_preset,
                valid_text_position_presets: scene.availableTextPositions,
            };
            return {
                result: JSON.stringify(errorPayload),
                isSuccess: false,
            } satisfies EnhancedToolResult;
        }
        return `Success: Set text_position_preset to ${text_position_preset} for scene ${sceneShortId}`;
    }
}
